import Experiments from '@wix/wix-experiments';
import { ViewPresets, ViewPreset, PRESETS } from './presets';
import { EXPERIMENTS } from '../stores/constants';

export const MAP_VERTICAL_PATH_TO_PRESET = {
  logo: PRESETS.LOGO_MAKER,
  [PRESETS.DAYFUL]: PRESETS.DAYFUL,
  [PRESETS.CODUX]: PRESETS.CODUX,
  [PRESETS.HOPP]: PRESETS.HOPP,
  [PRESETS.WIXEL]: PRESETS.WIXEL,
};

type PresetResolutionParams = {
  getQueryParam: (key: string) => string;
  baseDomain?: string;
  userType?: string;
  origin?: string;
  experiments: Experiments;
};

export type ResolvedPreset = {
  presetKey?: string;
  preset?: ViewPreset;
};

export const resolvePresetKey = ({
  getQueryParam,
  baseDomain,
  userType,
  origin,
  experiments,
}: PresetResolutionParams): string | undefined => {
  const getPresetFromQueryParams = () =>
    getQueryParam('preset') ||
    getQueryParam('customized') ||
    getQueryParam('color');

  const shouldPrioritizeUserType = experiments.enabled(
    'specs.ident.prioritizeUserTypeOverQueryParam',
  );

  let presetKey: string | undefined;

  if (shouldPrioritizeUserType) {
    if (userType && MAP_VERTICAL_PATH_TO_PRESET[userType]) {
      presetKey = MAP_VERTICAL_PATH_TO_PRESET[userType];
    } else {
      presetKey = getPresetFromQueryParams();
    }
  } else {
    presetKey = getPresetFromQueryParams();
  }

  if (!presetKey) {
    // Special handling for editorX
    if (baseDomain?.includes('editorx')) {
      presetKey = PRESETS.EDITORX;
    } else if (userType && MAP_VERTICAL_PATH_TO_PRESET[userType]) {
      presetKey = MAP_VERTICAL_PATH_TO_PRESET[userType];
    } else if (origin && baseDomain) {
      let splited = origin.split(`${baseDomain}/`);
      splited = splited?.[1]?.split('/');
      presetKey = splited?.[0];
    }
  }

  return presetKey;
};

export const resolvePreset = (
  params: PresetResolutionParams,
): ResolvedPreset => {
  const presetKey = resolvePresetKey(params);

  // Resolve preset from ViewPresets, trying exact match and case variations
  let preset: ViewPreset = {};
  let resolvedPresetKey: string | undefined = presetKey;
  if (presetKey) {
    preset =
      ViewPresets[presetKey] ||
      ViewPresets[presetKey.toLowerCase()] ||
      ViewPresets[presetKey.toUpperCase()] ||
      {};
    // If presetKey was set but doesn't resolve to a valid preset, treat it as undefined
    if (Object.keys(preset).length === 0) {
      console.log(
        '[PresetDetection] Preset key from URL path does not resolve to valid preset:',
        presetKey,
      );
      resolvedPresetKey = undefined;
    } else {
      console.log('[PresetDetection] Resolved preset from key:', presetKey);
    }
  }
  // If no preset was explicitly set and wix-2025 experiment is enabled, use wix-2025 as default
  const experimentName = EXPERIMENTS.SHOULD_ACTIVATE_WIX_2025_PRESET;
  const isWix2025ExperimentEnabled = params.experiments.enabled(experimentName);
  if (!resolvedPresetKey && isWix2025ExperimentEnabled) {
    console.log(
      '[PresetDetection] Applying wix-2025 preset as default due to experiment',
    );
    resolvedPresetKey = PRESETS.WIX_2025;
    preset = ViewPresets[PRESETS.WIX_2025] || {};
  }

  return {
    presetKey: resolvedPresetKey,
    preset,
  };
};

export const detectPreset = (experiments: Experiments): ResolvedPreset => {
  const urlParams = new URLSearchParams(window.location.search);
  return resolvePreset({
    getQueryParam: (key) => urlParams.get(key) ?? '',
    baseDomain: window.__BASE_DOMAIN__,
    userType: window.__USER_TYPE__ || '',
    origin: window.location.origin,
    experiments,
  });
};
